import { signal, Signal, computed, ReadonlySignal } from "@preact/signals";
import { createStore, get, set, keys, UseStore } from "idb-keyval";
import {
   FlashCard,
   FlashCardReview,
   loadFlashCardQuestions,
   makeReviewedCard,
   REVIEW_RESULTS,
   ReviewResult
} from "./flash-card-utils";

/**
 * An in-memory map of {id: Signal<FlashCard>} that also persists itself to indexed DB.
 */
class FlashCardStore {
   // The Container is static (Map), the values are reactive (Signals)
   public readonly cards = signal(new Map<string, Signal<FlashCard>>());
   private readonly store: UseStore;
   public problems: Signal<string[]> = signal([]);

   constructor() {
      // Namespaced store: DB='flash-cards-db', Store='reviews'
      this.store = createStore('flash-cards-db', 'reviews');

      // console.log("idb-keyval :: createStore('flash-cards-db', 'reviews')")
   }

   /**
    * Initialize the store: load static questions + dynamic DB history.
    */
   async init() {
      // console.log("idb-keyval :: init()")
      try {
         const { questions, problems } = await loadFlashCardQuestions("/assets/flashcards/questions.md");
         this.problems = signal(problems);
         const historyMap = await this.loadReviewsFromDB();

         // Build a temporary map so we only trigger ONE update at the end
         const tempMap = new Map<string, Signal<FlashCard>>();

         for (const q of questions) {
            const reviews = historyMap.get(q.id) || [];
            const card: FlashCard = { ...q, reviews };
            tempMap.set(q.id, signal(card));
         }

         // Update the main signal. This wakes up the UI.
         this.cards.value = tempMap;
      }
      catch (e) {
         console.error("Failed to load flash cards:", e);
      }
   }

   /**
    * Updates a card's review history and persists it.
    */
   submitReview(cardId: string, result: ReviewResult) {
      const cardSignal = this.cards.value.get(cardId);

      if (!cardSignal) {
         console.error(`Cannot review missing card: ${cardId}`);
         return;
      }

      // creates new card (doesn't mutate old one)
      const updatedCard = makeReviewedCard(cardSignal.value, result);

      // Update Signal (UI Re-renders only this card immediately)
      cardSignal.value = updatedCard;

      // Persist to DB (Side Effect)
      // We only save the 'reviews' array to disk, not the static prompts
      set(cardId, updatedCard.reviews, this.store)
         .catch(err => console.error(`DB save for card ID="${cardId}" failed:`, err));
   }

   /**
    * Returns a computed signal containing all cards matching the prefix.
    * Result is cached and only updates if the Map itself changes.
   */
   getCardsByPrefix(prefix: string): ReadonlySignal<Signal<FlashCard>[]> {
      return computed(() => {
         const matches: Signal<FlashCard>[] = [];
         for (const [id, cardSignal] of this.cards.value) {
            if (id.startsWith(prefix)) {
               matches.push(cardSignal);
            }
         }
         return matches;
      });
   }

   /**
    * Fetches all keys, retrieves their values, and validates the shape.
    */
   private async loadReviewsFromDB(): Promise<Map<string, FlashCardReview[]>> {
      const resultMap = new Map<string, FlashCardReview[]>();

      // 1. Get all keys (card IDs) currently in the DB store
      const dbKeys = await keys(this.store);

      // 2. Create a promise for each key to fetch its value
      const fetchPromises = dbKeys.map(async (key) => {
         const val = await get(key, this.store);
         return { key, val };
      });

      // 3. Wait for all reads to complete
      const results = await Promise.all(fetchPromises);

      // 4. Validate and build the map
      for (const { key, val } of results) {
         if (this.isValidHistory(val)) {
            // IDB keys can technically be numbers, ensure it's a string
            resultMap.set(String(key), val);
         } else {
            console.warn(`Skipping corrupted/invalid data for key "${key}":`, val);
         }
      }

      return resultMap;
   }

   /**
    * Light Type Guard: Ensures the data from DB is actually FlashCardReview[]
    */
   private isValidHistory(data: any): data is FlashCardReview[] {
      if (!Array.isArray(data)) return false;
      if (data.length === 0) return true;

      // Check all items to ensure it they look right.
      // idb-keyval preserves actual Date objects (structured cloning),
      // so we check instanceof Date, not typeof string.
      return data.every((sample) => (
         typeof sample === 'object' &&
         sample !== null &&
         'result' in sample &&
         REVIEW_RESULTS.has(sample.result) &&
         'date' in sample &&
         sample.date instanceof Date
      ));
   }
}

class FlashCardOptionsStore {
   private readonly store: UseStore;

   // 1. Private signals for reactivity
   private _mixMode = signal(false);
   private _defaultCollapsed = signal(false);

   constructor() {
      this.store = createStore('flash-cards-options-db', 'options');
      this.init();
   }

   // 2. Direct access via getters (enables autocomplete)
   get mixMode() { return this._mixMode.value; }
   get defaultCollapsed() { return this._defaultCollapsed.value; }

   // 3. Setters handle Logic + Signal + DB
   set mixMode(val: boolean) {
      this._mixMode.value = val;
      set('mixMode', val, this.store);
   }

   set defaultCollapsed(val: boolean) {
      this._defaultCollapsed.value = val;
      set('defaultCollapsed', val, this.store);
   }

   /**
    * Load initial values from DB.
    * If nothing is stored, it stays as the default value.
    */
   private async init() {
      const [mix, collapse] = await Promise.all([
         get<boolean>('mixMode', this.store),
         get<boolean>('defaultCollapsed', this.store)
      ]);

      if (mix !== undefined) this._mixMode.value = mix;
      if (collapse !== undefined) this._defaultCollapsed.value = collapse;
   }
}

/**
 * Singleton to be used by consumers.
 */
export const store = new FlashCardStore();
store.init();  // Fire immediately on load.

export const optionsStore = new FlashCardOptionsStore();
